import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import './GasDistributorDistribuicaoFollowupConfigModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de configuração específico para a lista "Distribuição Follow-up"
 * Permite configurar:
 * - Notificação para grupo quando lead entra na lista
 * - Mensagem automática para o cliente
 * - Regras de follow-up automático (mover para outra lista após X horas)
 * - Encaminhamento para atendimento quando cliente contata
 * - Automação de tags com IA (análise diária e aplicação automática)
 */
export default function GasDistributorDistribuicaoFollowupConfigModal({ list, onClose, onSave, projectId }) {
  const [loading, setLoading] = useState(false);
  const [grupoConfigurado, setGrupoConfigurado] = useState(null);
  const [conexaoConfigurada, setConexaoConfigurada] = useState(null);
  
  // Notificação para Grupo
  const [notificacaoAtivada, setNotificacaoAtivada] = useState(false);
  const [mensagemGrupo, setMensagemGrupo] = useState('');
  
  // Mensagem Automática para Cliente
  const [mensagemAutoAtivada, setMensagemAutoAtivada] = useState(false);
  const [mensagemAuto, setMensagemAuto] = useState('');
  const [usarIA, setUsarIA] = useState(false);
  const [instrucoesIA, setInstrucoesIA] = useState('');
  
  // Follow-up Automático
  const [followUpAtivado, setFollowUpAtivado] = useState(false);
  const [followUpHoras, setFollowUpHoras] = useState(24);
  const [listaDestino, setListaDestino] = useState('');
  const [listasProjeto, setListasProjeto] = useState([]);
  
  // Encaminhamento para Atendimento
  const [encaminharAtendimento, setEncaminharAtendimento] = useState(false);
  const [listaAtendimento, setListaAtendimento] = useState('');
  
  // Automação de Tags com IA
  const [automacaoTagsAtivada, setAutomacaoTagsAtivada] = useState(false);
  const [horarioExecucao, setHorarioExecucao] = useState('02:00');
  const [tagsConfiguradas, setTagsConfiguradas] = useState([]);
  const [tagsDisponiveis, setTagsDisponiveis] = useState([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [executandoAutomacao, setExecutandoAutomacao] = useState(false);
  const [logsAutomacao, setLogsAutomacao] = useState([]);
  
  // Carregar configurações do projeto
  useEffect(() => {
    const loadProjectConfig = async () => {
      try {
        const effectiveProjectId = projectId || list?.project_id;
        
        if (!effectiveProjectId) {
          console.error('projectId não disponível');
          return;
        }

        // Buscar projeto com settings
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings')
          .eq('id', effectiveProjectId)
          .maybeSingle();

        if (projectError) {
          console.error('Erro ao buscar projeto:', projectError);
        }

        // Buscar conexão vinculada ao projeto
        const connectionId = project?.settings?.gas_distributor?.connection_id;
        if (connectionId) {
          const { data: connection, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .maybeSingle();

          if (connError) {
            console.error('Erro ao buscar conexão:', connError);
          } else if (connection) {
            setConexaoConfigurada(connection);
          }
        }

        // Buscar config do gas_distributor (grupo)
        const { data: config, error } = await supabase
          .from('gas_distributor_config')
          .select('grupo_whatsapp_id, grupo_whatsapp_nome')
          .eq('project_id', effectiveProjectId)
          .maybeSingle();

        if (error) {
          console.error('Erro ao buscar configuração do grupo:', error);
          return;
        }

        if (config?.grupo_whatsapp_id) {
          setGrupoConfigurado({
            id: config.grupo_whatsapp_id,
            nome: config.grupo_whatsapp_nome || 'Grupo configurado'
          });
        }
      } catch (error) {
        console.error('Erro ao carregar configurações:', error);
      }
    };

    // Carregar listas do projeto
    const loadProjectLists = async () => {
      try {
        const effectiveProjectId = projectId || list?.project_id;
        const { data: lists, error } = await supabase
          .from('project_lists')
          .select('id, name')
          .eq('project_id', effectiveProjectId)
          .order('name', { ascending: true });

        if (error) throw error;
        setListasProjeto(lists || []);
      } catch (error) {
        console.error('Erro ao carregar listas:', error);
      }
    };

    loadProjectConfig();
    loadProjectLists();
  }, [projectId, list?.project_id]);

  // Carregar configuração da lista
  useEffect(() => {
    const loadListConfig = async () => {
      if (!list?.id) return;
      
      try {
        const { data: listData, error } = await supabase
          .from('project_lists')
          .select('metadata, automation_rules')
          .eq('id', list.id)
          .maybeSingle();

        if (error) throw error;

        const metadata = listData?.metadata || {};
        const rules = listData?.automation_rules || {};
        
        // Notificação para grupo
        const groupNotif = metadata.group_notification || {};
        setNotificacaoAtivada(groupNotif.enabled || false);
        setMensagemGrupo(groupNotif.message || '');
        
        // Mensagem automática
        setMensagemAutoAtivada(rules.auto_message_enabled || false);
        setMensagemAuto(rules.auto_message || '');
        setUsarIA(rules.use_ai_message || false);
        setInstrucoesIA(rules.ai_instructions || '');
        
        // Follow-up automático
        const followUp = metadata.follow_up || {};
        setFollowUpAtivado(followUp.enabled || false);
        setFollowUpHoras(followUp.delay_hours || 24);
        setListaDestino(followUp.target_list_id || '');
        
        // Encaminhamento para atendimento
        const encaminhamento = metadata.forward_to_attendance || {};
        setEncaminharAtendimento(encaminhamento.enabled || false);
        setListaAtendimento(encaminhamento.target_list_id || '');
      } catch (error) {
        console.error('Erro ao carregar configuração da lista:', error);
      }
    };

    loadListConfig();
  }, [list?.id]);

  // Carregar tags disponíveis e configuração de automação
  useEffect(() => {
    const loadTagsData = async () => {
      const effectiveProjectId = projectId || list?.project_id;
      if (!effectiveProjectId) return;

      setLoadingTags(true);
      try {
        // Buscar tags do projeto
        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('settings')
          .eq('id', effectiveProjectId)
          .maybeSingle();

        if (projectError) throw projectError;

        // tags_configuradas são objetos completos salvos no settings, não IDs
        const tagsConfig = projectData?.settings?.gas_distributor?.tags_configuradas || [];
        
        console.log('[FollowupConfig] Tags configuradas no projeto:', tagsConfig);

        // As tags já vêm como objetos completos do settings
        // Formato: { id, nome, descricao, cor, icone, ativa, regra_aplicacao }
        const tagsFormatadas = tagsConfig
          .filter(t => t.ativa !== false)
          .map(t => ({
            id: t.id,
            name: t.nome,
            description: t.descricao,
            metadata: {
              regra_aplicacao: t.regra_aplicacao,
              cor: t.cor,
              icone: t.icone
            }
          }));

        console.log('[FollowupConfig] Tags formatadas:', tagsFormatadas);
        setTagsDisponiveis(tagsFormatadas);

        // Buscar configuração de automação de follow-up
        const response = await fetch(`/api/gas-distributor/${effectiveProjectId}/followup-automation`, {
          headers: {
            'x-company-id': localStorage.getItem('companyId')
          }
        });

        const result = await response.json();

        if (result.success && result.automation) {
          setAutomacaoTagsAtivada(result.automation.enabled || false);
          setHorarioExecucao(result.automation.horario_execucao?.substring(0, 5) || '02:00');
          
          if (result.automation.tags) {
            setTagsConfiguradas(result.automation.tags.map(t => ({
              tag_id: t.tag_id,
              tag_name: t.tags?.name,
              lista_destino_id: t.lista_destino_id,
              lista_destino_name: t.lista_destino?.name,
              enabled: t.enabled
            })));
          }
        }

        // Buscar logs recentes
        const logsResponse = await fetch(`/api/gas-distributor/${effectiveProjectId}/followup-automation/logs?limit=5`, {
          headers: {
            'x-company-id': localStorage.getItem('companyId')
          }
        });

        const logsResult = await logsResponse.json();
        if (logsResult.success) {
          setLogsAutomacao(logsResult.logs || []);
        }

      } catch (error) {
        console.error('Erro ao carregar tags:', error);
      } finally {
        setLoadingTags(false);
      }
    };

    loadTagsData();
  }, [projectId, list?.project_id]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const metadata = list?.metadata || {};
      const updatedMetadata = {
        ...metadata,
        group_notification: {
          enabled: notificacaoAtivada,
          message: mensagemGrupo
        },
        follow_up: {
          enabled: followUpAtivado,
          delay_hours: parseInt(followUpHoras),
          target_list_id: listaDestino
        },
        forward_to_attendance: {
          enabled: encaminharAtendimento,
          target_list_id: listaAtendimento
        }
      };

      const automationRules = {
        auto_message_enabled: mensagemAutoAtivada,
        auto_message: mensagemAuto,
        use_ai_message: usarIA,
        ai_instructions: instrucoesIA
      };

      const { error } = await supabase
        .from('project_lists')
        .update({
          metadata: updatedMetadata,
          automation_rules: automationRules,
          updated_at: new Date().toISOString()
        })
        .eq('id', list.id);

      if (error) throw error;

      // Salvar configuração de automação de tags
      const effectiveProjectId = projectId || list?.project_id;
      if (effectiveProjectId) {
        const response = await fetch(`/api/gas-distributor/${effectiveProjectId}/followup-automation`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-company-id': localStorage.getItem('companyId')
          },
          body: JSON.stringify({
            enabled: automacaoTagsAtivada,
            horario_execucao: horarioExecucao,
            tags: tagsConfiguradas
          })
        });

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Erro ao salvar automação de tags');
        }
      }

      alert('Configurações salvas com sucesso!');
      onSave({ ...list, metadata: updatedMetadata, automation_rules: automationRules });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Função para adicionar tag à configuração
  const handleAddTag = (tagId) => {
    const tag = tagsDisponiveis.find(t => t.id === tagId);
    if (!tag) return;

    if (!tagsConfiguradas.find(t => t.tag_id === tagId)) {
      setTagsConfiguradas([...tagsConfiguradas, {
        tag_id: tagId,
        tag_name: tag.name,
        lista_destino_id: null,
        lista_destino_name: null,
        enabled: true
      }]);
    }
  };

  // Função para remover tag da configuração
  const handleRemoveTag = (tagId) => {
    setTagsConfiguradas(tagsConfiguradas.filter(t => t.tag_id !== tagId));
  };

  // Função para atualizar lista destino de uma tag
  const handleUpdateTagLista = (tagId, listaId) => {
    const lista = listasProjeto.find(l => l.id === listaId);
    setTagsConfiguradas(tagsConfiguradas.map(t => 
      t.tag_id === tagId 
        ? { ...t, lista_destino_id: listaId, lista_destino_name: lista?.name }
        : t
    ));
  };

  // Função para executar automação manualmente
  const handleExecutarAutomacao = async () => {
    const effectiveProjectId = projectId || list?.project_id;
    if (!effectiveProjectId) return;

    setExecutandoAutomacao(true);
    try {
      const response = await fetch(`/api/gas-distributor/${effectiveProjectId}/followup-automation/execute`, {
        method: 'POST',
        headers: {
          'x-company-id': localStorage.getItem('companyId')
        }
      });

      const result = await response.json();

      if (result.success) {
        alert(`Automação executada!\n${result.logData?.cards_analisados || 0} cards analisados\n${result.logData?.tags_aplicadas || 0} tags aplicadas`);
      } else {
        alert('Erro ao executar: ' + result.error);
      }
    } catch (error) {
      alert('Erro ao executar: ' + error.message);
    } finally {
      setExecutandoAutomacao(false);
    }
  };

  return (
    <div className="followup-modal-overlay" onClick={onClose}>
      <div className="followup-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="followup-modal-header">
          <div className="followup-modal-title-section">
            <AppIcon name="bolt" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="followup-modal-content">
          {/* Seção: Informações do Grupo e Conexão */}
          <div className="followup-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="group" />
                <h4>Configurações do Projeto</h4>
              </div>
            </div>

            {/* Grupo Configurado */}
            <div className="info-box grupo-info-box">
              <div className="info-header">
                <AppIcon name="groups" className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Grupo Configurado no Projeto</span>
                  {grupoConfigurado ? (
                    <>
                      <span className="info-nome">{grupoConfigurado.nome}</span>
                      <span className="info-id">{grupoConfigurado.id}</span>
                    </>
                  ) : (
                    <span className="info-nao-configurado">Nenhum grupo configurado</span>
                  )}
                </div>
              </div>
            </div>

            {/* Conexão Configurada */}
            <div className="info-box conexao-info-box">
              <div className="info-header">
                <AppIcon name="link" className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Conexão Vinculada ao Projeto</span>
                  {conexaoConfigurada ? (
                    <>
                      <span className="info-nome">{conexaoConfigurada.name}</span>
                      <span className="info-numero">{conexaoConfigurada.number}</span>
                    </>
                  ) : (
                    <span className="info-nao-configurada">Nenhuma conexão vinculada</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Seção: Notificação para Grupo */}
          <div className="followup-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="notifications" />
                <h4>Notificação para Grupo</h4>
              </div>
            </div>

            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={notificacaoAtivada}
                  onChange={(e) => setNotificacaoAtivada(e.target.checked)}
                  disabled={!grupoConfigurado}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {notificacaoAtivada 
                    ? '✅ Notificação ativada' 
                    : '❌ Notificação desativada'}
                </span>
              </label>
              <small className="toggle-description">
                Quando um lead entrar nesta lista, uma mensagem será enviada para o grupo
              </small>
            </div>

            {notificacaoAtivada && (
              <div className="mensagem-section">
                <label className="mensagem-label">Mensagem para Grupo (opcional)</label>
                <textarea
                  value={mensagemGrupo}
                  onChange={(e) => setMensagemGrupo(e.target.value)}
                  placeholder="📋 Novo Lead em Follow-up&#10;&#10;👤 Cliente: {cliente}&#10;&#10;📍 Endereço: {endereco}&#10;&#10;Este lead está apto para receber novos pedidos."
                  rows={5}
                  className="mensagem-textarea"
                />
                <small className="mensagem-hint">
                  Variáveis: {'{cliente}'}, {'{endereco}'}, {'{telefone}'}
                </small>
              </div>
            )}
          </div>

          {/* Seção: Mensagem Automática para Cliente */}
          <div className="followup-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="chat" />
                <h4>Mensagem Automática para Cliente</h4>
              </div>
            </div>

            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={mensagemAutoAtivada}
                  onChange={(e) => setMensagemAutoAtivada(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {mensagemAutoAtivada 
                    ? '✅ Mensagem automática ativada' 
                    : '❌ Mensagem automática desativada'}
                </span>
              </label>
              <small className="toggle-description">
                Envia uma mensagem automaticamente quando o lead entra nesta lista
              </small>
            </div>

            {mensagemAutoAtivada && (
              <>
                <div className="tool-enable-section">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={usarIA}
                      onChange={(e) => setUsarIA(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                    <span className="toggle-text">
                      {usarIA ? '🤖 Usar IA para gerar mensagem' : '📝 Usar mensagem personalizada'}
                    </span>
                  </label>
                </div>

                {usarIA ? (
                  <div className="mensagem-section">
                    <label className="mensagem-label">Instruções para a IA</label>
                    <textarea
                      value={instrucoesIA}
                      onChange={(e) => setInstrucoesIA(e.target.value)}
                      placeholder="Ex: Envie uma mensagem amigável agradecendo pelo contato anterior e perguntando se precisa de algo."
                      rows={3}
                      className="mensagem-textarea"
                    />
                  </div>
                ) : (
                  <div className="mensagem-section">
                    <label className="mensagem-label">Mensagem</label>
                    <textarea
                      value={mensagemAuto}
                      onChange={(e) => setMensagemAuto(e.target.value)}
                      placeholder="Olá {cliente}! 👋&#10;&#10;Agradecemos seu contato anterior. Estamos à disposição para novos pedidos!&#10;&#10;Até logo! 🙏"
                      rows={5}
                      className="mensagem-textarea"
                    />
                    <small className="mensagem-hint">
                      Variáveis: {'{cliente}'}, {'{telefone}'}
                    </small>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Seção: Follow-up Automático */}
          <div className="followup-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="schedule" />
                <h4>Follow-up Automático</h4>
              </div>
            </div>

            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={followUpAtivado}
                  onChange={(e) => setFollowUpAtivado(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {followUpAtivado 
                    ? '✅ Follow-up automático ativado' 
                    : '❌ Follow-up automático desativado'}
                </span>
              </label>
              <small className="toggle-description">
                Move automaticamente o lead para outra lista após um período
              </small>
            </div>

            {followUpAtivado && (
              <div className="followup-config-row">
                <div className="form-group">
                  <label>Após</label>
                  <div className="input-with-suffix">
                    <input
                      type="number"
                      value={followUpHoras}
                      onChange={(e) => setFollowUpHoras(e.target.value)}
                      min="1"
                      className="input-horas"
                    />
                    <span className="input-suffix">horas</span>
                  </div>
                </div>
                <div className="form-group">
                  <label>Mover para</label>
                  <select
                    value={listaDestino}
                    onChange={(e) => setListaDestino(e.target.value)}
                    className="select-lista"
                  >
                    <option value="">Selecione uma lista</option>
                    {listasProjeto
                      .filter(l => l.id !== list?.id)
                      .map(l => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Seção: Encaminhamento para Atendimento */}
          <div className="followup-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="support_agent" />
                <h4>Encaminhamento para Atendimento</h4>
              </div>
            </div>

            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={encaminharAtendimento}
                  onChange={(e) => setEncaminharAtendimento(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {encaminharAtendimento 
                    ? '✅ Encaminhamento ativado' 
                    : '❌ Encaminhamento desativado'}
                </span>
              </label>
              <small className="toggle-description">
                Quando o cliente entrar em contato, move automaticamente para a lista de atendimento
              </small>
            </div>

            {encaminharAtendimento && (
              <div className="form-group">
                <label>Lista de Atendimento</label>
                <select
                  value={listaAtendimento}
                  onChange={(e) => setListaAtendimento(e.target.value)}
                  className="select-lista"
                >
                  <option value="">Selecione uma lista</option>
                  {listasProjeto
                    .filter(l => l.name === 'Cliente em Atendimento' || l.name.includes('Atendimento'))
                    .map(l => (
                      <option key={l.id} value={l.id}>{l.name}</option>
                    ))}
                </select>
                <small className="form-hint">
                  O lead será movido para esta lista quando o cliente enviar uma mensagem
                </small>
              </div>
            )}
          </div>

          {/* Seção: Automação de Tags com IA */}
          <div className="followup-section">
            <div className="section-header">
              <div className="header-left">
                <AppIcon name="auto_awesome" />
                <h4>Automação de Tags com IA</h4>
              </div>
            </div>

            <div className="tool-enable-section">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  checked={automacaoTagsAtivada}
                  onChange={(e) => setAutomacaoTagsAtivada(e.target.checked)}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-text">
                  {automacaoTagsAtivada 
                    ? '✅ Automação de tags ativada' 
                    : '❌ Automação de tags desativada'}
                </span>
              </label>
              <small className="toggle-description">
                Analisa clientes diariamente com IA e aplica tags automaticamente
              </small>
            </div>

            {automacaoTagsAtivada && (
              <>
                <div className="followup-config-row">
                  <div className="form-group">
                    <label>Horário de Execução</label>
                    <input
                      type="time"
                      value={horarioExecucao}
                      onChange={(e) => setHorarioExecucao(e.target.value)}
                      className="input-horas"
                    />
                    <small className="form-hint">A automação será executada diariamente neste horário</small>
                  </div>
                </div>

                {/* Tags configuradas */}
                <div className="tags-config-section">
                  <label className="mensagem-label">Tags para Aplicar Automaticamente</label>
                  
                  {loadingTags ? (
                    <p>Carregando tags...</p>
                  ) : (
                    <>
                      {/* Tags já adicionadas */}
                      {tagsConfiguradas.length > 0 && (
                        <div className="tags-list">
                          {tagsConfiguradas.map((tag, index) => (
                            <div key={tag.tag_id} className="tag-config-item">
                              <div className="tag-info">
                                <span className="tag-name">{tag.tag_name}</span>
                                <select
                                  value={tag.lista_destino_id || ''}
                                  onChange={(e) => handleUpdateTagLista(tag.tag_id, e.target.value)}
                                  className="select-lista-small"
                                >
                                  <option value="">Não mover</option>
                                  {listasProjeto.map(l => (
                                    <option key={l.id} value={l.id}>{l.name}</option>
                                  ))}
                                </select>
                              </div>
                              <button 
                                className="btn-remove-tag"
                                onClick={() => handleRemoveTag(tag.tag_id)}
                              >
                                <AppIcon name="close" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Adicionar nova tag */}
                      {tagsDisponiveis.length > tagsConfiguradas.length && (
                        <div className="add-tag-section">
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAddTag(e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="select-lista"
                          >
                            <option value="">+ Adicionar tag</option>
                            {tagsDisponiveis
                              .filter(t => !tagsConfiguradas.find(tc => tc.tag_id === t.id))
                              .map(t => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                          </select>
                        </div>
                      )}

                      {tagsDisponiveis.length === 0 && (
                        <p className="no-tags-message">Configure tags no projeto primeiro</p>
                      )}
                    </>
                  )}
                </div>

                {/* Botão para executar manualmente */}
                <div className="execute-section">
                  <button 
                    className="btn-execute"
                    onClick={handleExecutarAutomacao}
                    disabled={executandoAutomacao}
                  >
                    <AppIcon name="play_arrow" />
                    {executandoAutomacao ? 'Executando...' : 'Executar Agora'}
                  </button>
                  <small className="form-hint">Testa a automação manualmente</small>
                </div>

                {/* Logs recentes */}
                {logsAutomacao.length > 0 && (
                  <div className="logs-section">
                    <label className="mensagem-label">Últimas Execuções</label>
                    <div className="logs-list">
                      {logsAutomacao.map(log => (
                        <div key={log.id} className="log-item">
                          <span className="log-date">
                            {new Date(log.data_execucao).toLocaleString('pt-BR')}
                          </span>
                          <span className="log-stats">
                            {log.cards_analisados} analisados | {log.tags_aplicadas} tags | {log.cards_movidos} movidos
                          </span>
                          <span className={`log-status ${log.status}`}>
                            {log.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Informações sobre a lista */}
          <div className="followup-section info-section">
            <h4>
              <AppIcon name="info" />
              Sobre esta lista
            </h4>
            <p className="info-text">
              A lista <strong>"Distribuição Follow-up"</strong> recebe leads que estão aptos para receber novos pedidos.
              Configure as ferramentas acima para automatizar o acompanhamento e encaminhar para atendimento quando o cliente entrar em contato.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="followup-modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Configuração'}
          </button>
        </div>
      </div>
    </div>
  );
}
